import { CdnPriorityLimits } from '../Utils/Constants';
import { LoaderEvent } from '../Utils/Constants';
import Cdn from './Cdn';
import Utils from '../Utils/Utils';
import Emitter from '../Utils/Emitter';
import CDNSelectorBusinessObject from '../BusinessObjects/CDNSelectorBusinessObject';
import TestProfilesBusinessObject from '../BusinessObjects/TestProfilesBusinessObject';
import Logger from './../Utils/Logger';
/**
 * @class
 * @description Class controlling the requests from the player to CDNs.
 * It has different algorithms to choose to decide which CDN has to be used for every request.
 * @exports CDNLoader
 */
export default class CDNLoader extends Emitter {
    /**
     * Constructs CDNLoader
     * @param {Options} options Options object.
     */
    constructor(options, resource) {
        super();
        this._bandwidthThreshold = CdnPriorityLimits.default;
        this._cdnList = new Map();
        this._activeCdnRequests = new Map();
        this._activeRequests = new Map();
        this._failedRequests = 0;
        this._chunkCount = 0;
        this._highestChunkBandwidth = 0;
        this._highestRenditionBandwidth = 0;
        this._cdnLastInstantBandwith = new Map();
        this._cdnInstantBandwith = new Map();
        this._cdnPeakBandwidth = new Map();
        this._retries = new Map();
        this._totalDownloadedBytes = 0;
        this._firstRequestTime = 0;
        this._lastChunkTime = 0;
        this._lastChunksLapse = 0;
        this.currentRenditionBandwidth = 0;
        this.currentRenditionPos = 0;
        this.renditionCount = 0;
        this.initializated = false;
        this.monitoringStarted = false;
        this._options = options;
        this._resource = resource;
    }
    getSecure() {
        if (this._secure == undefined)
            return false;
        return this._secure;
    }
    getActiveRequests() {
        return this._activeRequests;
    }
    /**
     * For initial workflow and resets, asks to the API for the list of available
     * CDNs with the content we want to reproduce.
     * It creates the CDN list object and fills it with the CDNs received.
     * Also calculates the relative score.
     * @public
     */
    setSettings(resp, originalHost) {
        var _a;
        if (this.initializated)
            return this.updateSettings(resp, originalHost);
        // let  index = 0;
        this._cdnList = new Map();
        try {
            let resourceInfo = Utils.getRouteAndFilename(this._options.resource);
            this._secure = this._options.resource.indexOf('https://') > -1 || undefined;
            this._resource.fileName = resourceInfo.fileName;
            this._resource.route = resourceInfo.route;
            this._responseUUID = resp.UUID;
            this._method = resp.cdnBalanceMethod;
            this._activeSwitching = resp.activeSwitching;
            let totalScore = 0;
            let responseContainsOriginal = false;
            const cdnList = ((_a = resp.providers) === null || _a === void 0 ? void 0 : _a.concat(resp.discarded || [])) || [];
            cdnList.forEach((cdn) => {
                totalScore += cdn.score || 0;
            });
            cdnList.forEach((cdn) => {
                const score = cdn.score || 0;
                const route = Utils.getRouteAndFilename(cdn.url).route;
                responseContainsOriginal = responseContainsOriginal || route === originalHost; // true if already true or if CDN is the original
                let newCdn = new Cdn(cdn.provider, cdn.name, route, score, route === originalHost);
                newCdn.host = cdn.host;
                this._cdnList.set(cdn.name, newCdn);
                this._activeCdnRequests[cdn.name] = 0;
            });
        }
        catch (err) {
            console.warn(err);
        }
        let originalCdn = new Cdn('ORIGINAL', 'Original', originalHost, 0, true);
        originalCdn.usedOnce = true;
        this._cdnList.set('Original', originalCdn);
        this.initializated = true;
    }
    updateSettings(resp, originalHost) {
        var _a;
        this._method = resp.cdnBalanceMethod;
        this._activeSwitching = resp.activeSwitching;
        let responseContainsOriginal = false;
        const cdnList = ((_a = resp.providers) === null || _a === void 0 ? void 0 : _a.concat(resp.discarded || [])) || [];
        // If doesnt exist create a new one
        cdnList.forEach((cdn) => {
            let currentCDN = this._cdnList.get(cdn.name);
            if (!currentCDN) {
                const score = cdn.score || 0;
                const route = Utils.getRouteAndFilename(cdn.url).route;
                responseContainsOriginal = responseContainsOriginal || route === originalHost; // true if already true or if CDN is the original
                this._cdnList.set(cdn.name, new Cdn(cdn.provider, cdn.name, route, score, route === originalHost));
                this._activeCdnRequests[cdn.name] = 0;
            }
            else {
                currentCDN.active = true;
            }
        });
        this._cdnList.forEach((cdn) => {
            let found = false;
            for (let i = 0; i < cdnList.length; i++) {
                if (cdnList[i].name == cdn.name)
                    found = true;
            }
            if (!found)
                cdn.active = false;
        });
    }
    startMonitoring() {
        if (this.monitoringStarted)
            return;
        this.monitoringStarted = true;
        TestProfilesBusinessObject.getInstance().monitor(this._cdnList);
    }
    updateCDNStatsOnFailure(cdnName, segment) {
        this.updateProgressStats();
        const cdn = this._cdnList.get(cdnName);
        this._activeCdnRequests[cdnName]--;
        if (segment.retries == 0) {
            this._failedRequests++;
        }
        if (cdn != undefined) {
            cdn.retries--;
        }
        if (cdn)
            cdn.banned = true;
        if (cdn) {
            segment.failed = true;
            cdn.addResp(segment);
            cdn.failures++;
            cdn.totalFail++;
            cdn.usedOnce = true;
        }
    }
    removeActiveSegment(segment) {
        this._activeRequests.delete(segment.id);
        this._activeCdnRequests[segment.cdnName]--;
        this._chunkCount++;
    }
    updateCDNStatsOnSuccess(segment) {
        var _a;
        this.updateProgressStats();
        //segment.failed = false;
        if (segment.progressDownloadedBytes != undefined) {
            this._totalDownloadedBytes = this._totalDownloadedBytes + segment.size;
            if (segment.duration != 0) {
                let bandwidth = (segment.size * 8) / segment.duration;
                if (this._highestChunkBandwidth < bandwidth) {
                    this._highestChunkBandwidth = bandwidth;
                }
            }
            (_a = this._cdnList.get((segment === null || segment === void 0 ? void 0 : segment.cdnName) || '')) === null || _a === void 0 ? void 0 : _a.addResp(segment);
        }
    }
    getCdnList() {
        return this._cdnList;
    }
    updateProgressStats() {
        let cdnInstantBandwith = new Map();
        let cdnPeakBandwidth = new Map();
        this._activeRequests.forEach((videoSegment) => {
            let instantBandwidth = videoSegment.instantBandwidth;
            let profileName = videoSegment.cdnName;
            let profileBandwidth = cdnInstantBandwith.get(profileName);
            if (profileBandwidth == undefined) {
                profileBandwidth = 0;
            }
            profileBandwidth = profileBandwidth + instantBandwidth;
            cdnInstantBandwith.set(profileName, profileBandwidth);
        });
        this._cdnInstantBandwith = cdnInstantBandwith;
        cdnInstantBandwith.forEach((value, key) => {
            let peak = this._cdnPeakBandwidth.get(key);
            if (peak == undefined) {
                this._cdnPeakBandwidth.set(key, value);
            }
            else if (peak < value) {
                this._cdnPeakBandwidth.set(key, value);
            }
        });
        cdnInstantBandwith.forEach((value, key) => {
            if (value != 0)
                this._cdnLastInstantBandwith.set(key, value);
        });
        let peak = 0;
        cdnPeakBandwidth.forEach((value, key) => {
            peak = peak + value;
        });
    }
    updateUrl(segment, cdnName) {
        var _a, _b, _c, _d;
        Logger.log("Update url -> " + segment.url);
        if (segment.isManifest)
            return;
        if (cdnName) {
            let cdn = this._cdnList.get(cdnName);
            if (cdn) {
                let finalUrl = Utils.findPathMach(segment.url, ((_a = this._resource) === null || _a === void 0 ? void 0 : _a.route) || '', cdn);
                return finalUrl;
            }
        }
        let cdnListCloned = new Map();
        cdnListCloned = new Map(this._cdnList);
        let finalUrl = "";
        let selectedCdnName = "";
        let segmentBandwidth = ((_b = segment.rendition) === null || _b === void 0 ? void 0 : _b.bandwidth) || 0;
        if (this._highestRenditionBandwidth < segmentBandwidth)
            this._highestRenditionBandwidth = segmentBandwidth;
        while (!finalUrl || finalUrl == "") {
            let cdnSelectorBusinessObject = new CDNSelectorBusinessObject(cdnListCloned, this._bandwidthThreshold, this._highestRenditionBandwidth, this._activeSwitching);
            selectedCdnName = cdnSelectorBusinessObject.getCDN(segment);
            Logger.log("Selected CDN -> " + selectedCdnName);
            let selectedCdn = this._cdnList.get(selectedCdnName);
            try {
                if (selectedCdn && selectedCdnName != "Original") {
                    finalUrl = Utils.findPathMach(segment.url, ((_c = this._resource) === null || _c === void 0 ? void 0 : _c.route) || '', selectedCdn);
                    if (finalUrl == '') {
                        Logger.log("Problems updating url -> " + segment.url + " / " + ((_d = this._resource) === null || _d === void 0 ? void 0 : _d.route) + " / " + selectedCdn.name);
                    }
                    if (finalUrl == '' && selectedCdn.provider == 'STREBOOS') {
                        let baseUrl = selectedCdn.url;
                        let path = (new URL(segment.url)).pathname;
                        if (baseUrl.endsWith("/") && path.startsWith("/"))
                            finalUrl = baseUrl + path.substring(1, path.length);
                        else
                            finalUrl = baseUrl + path.substring(path.length);
                    }
                }
                else if (selectedCdnName == "Original") {
                    finalUrl = segment.url;
                }
            }
            catch (e) { }
            cdnListCloned.delete(selectedCdnName);
        }
        this._activeCdnRequests[selectedCdnName]++;
        let newCdn = this._cdnList.get(selectedCdnName);
        let params = new URL(finalUrl).search;
        if (newCdn && newCdn.provider == 'STREBOOS' && !params.includes('orresource=')) {
            if (params && params.startsWith('?')) {
                finalUrl = finalUrl + '&orresource=' + segment.origRes;
            }
            else {
                finalUrl = finalUrl + '?orresource=' + segment.origRes;
            }
        }
        segment.finalUrl = finalUrl;
        if (newCdn) {
            segment.changeCDN(finalUrl, newCdn.name, newCdn.provider);
        }
        Logger.log("New url -> " + finalUrl);
        return finalUrl;
    }
    /**
     * Returns an object with the CDN data stats of the current content/view.
     * @returns {CdnLoaderStats} CDN info object.
     * @public
     */
    getStats() {
        const cdns = [];
        let totalDownloadedBytes = 0;
        let totalBandwidth = 0;
        let totalPeakBandwidth = 0;
        let totalInstantBandwidth = 0;
        let totalDownloadedChunks = 0;
        let totalFailures = 0;
        let totalRetries = 0;
        let usedCDNs = 0;
        this._cdnList.forEach((cdn) => {
            let bandwidth = ((cdn.downloadedBytes || 0) / ((cdn.downloadMillis || 1) / 1000)) * 8;
            let scoringBandwidth = ((cdn.downloadedBytesForScoring || 0) / ((cdn.downloadTimeForScoring || 1) / 1000)) * 8;
            if (bandwidth == undefined)
                bandwidth = 0;
            let ptb = (this._totalDownloadedBytes / ((Date.now() - this._firstRequestTime) / 1000)) * 8;
            let peakBandwidth = this._cdnPeakBandwidth.get(cdn.name) || 0;
            let instantBandwidth = this._cdnInstantBandwith.get(cdn.name) || 0;
            let lastInstantBandwidth = this._cdnLastInstantBandwith.get(cdn.name) || 0;
            if (bandwidth > 0) {
                usedCDNs++;
            }
            cdns.push({
                downloadedBytes: cdn.downloadedBytes,
                downloadedChunks: cdn.downloadedChunks,
                bandwidth: Math.trunc(bandwidth),
                instantBandwidth: Math.trunc(instantBandwidth),
                lastInstantBandwidth: Math.trunc(lastInstantBandwidth),
                peakBandwidth: Math.trunc(peakBandwidth),
                failures: cdn.failures,
                totalFailures: cdn.totalFail,
                retries: cdn.retries,
                downloadTime: cdn.downloadMillis,
                provider: cdn.provider,
                profileName: cdn.name,
                isActive: cdn.active,
                ptb: ptb,
                usedOnce: cdn.usedOnce,
                hugeChunks: cdn.hugeChunks,
                firstHugeChunkSize: cdn.firstHugeChunkSize,
                lastChunksLapse: this._lastChunksLapse,
                maxBandwidth: cdn.maxBandwidth,
                banned: cdn.banned,
                lastBandwidth: cdn.lastPreferedVideoBandwidth,
                scoringBandwidth: scoringBandwidth,
            });
            totalDownloadedBytes += cdn.downloadedBytes;
            totalBandwidth += bandwidth;
            totalPeakBandwidth += peakBandwidth;
            totalInstantBandwidth += instantBandwidth;
            totalDownloadedChunks += cdn.downloadedChunks;
            totalFailures += cdn.failures,
                totalRetries += cdn.retries;
        });
        return {
            responseUUID: this._responseUUID,
            totalDownloadedBytes: totalDownloadedBytes,
            totalDownloadedChunks: totalDownloadedChunks,
            totailFailures: totalFailures,
            totalRetries: totalRetries,
            activeSwitching: this._activeSwitching || 'false',
            mode: this._method || '',
            totalBandwidth: Math.trunc(totalBandwidth),
            totalInstantBandwidth: Math.trunc(totalInstantBandwidth),
            totalPeakBandwidth: Math.trunc(totalPeakBandwidth),
            cdns,
            currentRenditionBandwidth: this.currentRenditionBandwidth,
            currentRenditionPos: this.currentRenditionPos,
            renditionCount: this.renditionCount,
        };
    }
    /**
     * CAUTION: if you try to disable many CDN you may break the view!
     * Enables/disables the use of a specific CDN.
     * You can get the name of the CDNs using getStats().cdn
     * @param {string} name Cdn name to be disabled/enabled.
     * @param {boolean} mode True to enable, False to disable.
     * @returns {boolean} True if change was applied, false if not.
     * @public
     */
    enableCDN(name, enable) {
        // First, checks that at least one cdn will be active when trying to disable
        let changed = false;
        let enabledCDNs = 0;
        if (!enable) {
            this._cdnList.forEach((cdn) => {
                if (cdn.active)
                    enabledCDNs++;
            });
        }
        if (enable || enabledCDNs > 1) {
            this._cdnList.forEach((cdn) => {
                if (cdn.name === name && cdn.active !== enable) {
                    cdn.active = enable;
                    changed = true;
                }
            });
        }
        return changed;
    }
    setMaxBandwidth(name, bandwidth) {
        let cdn = this._cdnList.get(name);
        if (cdn != undefined)
            cdn.maxBandwidth = bandwidth;
        else
            return false;
        this.emit(LoaderEvent.Updated, {
            loader: this,
        });
        return true;
    }
    /*private static setResourceTimingBufferSize(maxSize: number) {
      if (typeof performance !== 'undefined' && typeof performance.setResourceTimingBufferSize == 'function') {
        performance.setResourceTimingBufferSize(maxSize);
      }
    }*/
    getMaxBandwidth(cdnName) {
        let cdn = this._cdnList.get(cdnName);
        if (cdn != undefined)
            return cdn.maxBandwidth;
        return 0;
    }
}
